import React, { useState, useEffect, useCallback } from "react";
import DoctorListTable from "../components/doctorListTable";
import "../page/doctorList.css";
import DoctorListHeader from "../components/doctorListHeader";
import ModalAddDoctor from "../components/modalAddDoctor";
import ModalDeleteConfirmation from "../../../components/modal/modalDeleteConfirmation";
import PaginationFooter from "../../../components/footer/paginationFooter";
import { Constant } from "../../../helper/constant";
import { useNavigate } from "react-router-dom";

const DoctorList = () => {
  // =====================
  // Constant Definition
  // =====================
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [isOpenModalAddDoctor, setIsOpenModalAddDoctor] = useState(false);
  const [selectedDoctorData, setSelectedDoctorData] = useState({
    doctor_id: "",
    doctor_name: "",
    doctor_specialist: {
      doctor_specialist_id: "",
      doctor_specialist_name: "",
    },
  });
  const [doctorData, setDoctorData] = useState(null);
  const [isOpenModalDeleteDoctor, setIsOpenModalDeleteDoctor] = useState(false);
  const [idDoctorToDelete, setIdDoctorToDelete] = useState("");
  const [searchDoctorKeyword, setSearchDoctorKeyword] = useState("");
  // =====================
  // Function Definition
  // =====================

  const addNewDoctor = () => {
    resetSelectedDoctorDataValue();
    setIsOpenModalAddDoctor(true);
  };

  const onEditDoctor = (doctorData) => {
    setSelectedDoctorData(doctorData);
    setIsOpenModalAddDoctor(true);
  };

  const onDeleteDoctor = (doctorData) => {
    setIsOpenModalDeleteDoctor(true);
    setIdDoctorToDelete(doctorData.doctor_id);
  };

  const onDeleteDoctorConfirmed = () => {
    setIsOpenModalDeleteDoctor(false);
    deleteDoctor();
  };

  const onCloseModalDeleteDoctor = () => {
    setIdDoctorToDelete("");
    setIsOpenModalDeleteDoctor(false);
  };

  const onCloseModalAddDoctor = () => {
    resetSelectedDoctorDataValue();
    setIsOpenModalAddDoctor(false);
  };

  const onSubmitNewDoctor = (doctorData, isEditData) => {
    setIsOpenModalAddDoctor(false);
    if (isEditData) {
      editDoctor(doctorData);
    } else {
      createNewDoctor(doctorData);
    }
  };

  const resetSelectedDoctorDataValue = () => {
    setSelectedDoctorData({
      doctor_id: "",
      doctor_name: "",
      doctor_specialist: {
        doctor_specialist_id: "",
        doctor_specialist_name: "",
      },
    });
  };

  const onNextPageClicked = () => {
    if (page < totalPage) {
      setPage((prevPage) => {
        return prevPage + 1;
      });
    }
  };

  const onPreviousPageClicked = () => {
    if (page !== 1) {
      setPage((prevPage) => {
        return prevPage - 1;
      });
    }
  };

  // =====================
  // Request Method
  // =====================

  const getDoctorData = useCallback(async () => {
    try {
      localStorage.setItem("searchValue", "");
      setIsLoading(true);
      let url = Constant.BASE_API_URL + "/dokter?page=" + page;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });

      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      if (!data.doctors) {
        setDoctorData(null);
      } else {
        setDoctorData(data);
      }
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
    } catch (error) {
      setDoctorData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, navigate]);

  const createNewDoctor = async (doctorData) => {
    try {
      setIsLoading(true);
      const response = await fetch(Constant.BASE_API_URL + "/dokter", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
        body: JSON.stringify({
          doctor_name: doctorData.doctor_name,
          doctor_specialist_id:
            doctorData.doctor_specialist.doctor_specialist_id,
        }),
      });
      if (!response.ok) {
        alert("Terdapat Kesalahan Dalam Memproses Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getDoctorData();
    }
  };

  const editDoctor = async (doctorData) => {
    try {
      setIsLoading(true);
      const doctorId = doctorData.doctor_id;
      const response = await fetch(
        Constant.BASE_API_URL + "/dokter/" + doctorId,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
          body: JSON.stringify({
            doctor_name: doctorData.doctor_name,
            doctor_specialist_id:
              doctorData.doctor_specialist.doctor_specialist_id,
          }),
        }
      );
      if (!response.ok) {
        alert("Gagal Mengubah Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      getDoctorData();
    }
  };

  const deleteDoctor = async () => {
    try {
      setIsLoading(true);
      const doctorId = idDoctorToDelete;
      const response = await fetch(
        Constant.BASE_API_URL + "/dokter/" + doctorId,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: "bearer " + localStorage.getItem("token"),
          },
        }
      );
      if (!response.ok) {
        alert("Gagal Menghapus Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
    } finally {
      setIdDoctorToDelete("");
      getDoctorData();
    }
  };
  const searchDoctor = (searchText) => {
    setSearchDoctorKeyword(searchText);
    setPage(1);
  };

  const getDoctorSearchData = useCallback(async () => {
    try {
      setIsLoading(true);

      let url = new URL(Constant.BASE_API_URL + "/dokter/search");
      url.searchParams.append("keyword", searchDoctorKeyword);
      url.searchParams.append("page", page);

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: "bearer " + localStorage.getItem("token"),
        },
      });
      if (!response.ok) {
        alert("Gagal Menerima Data");
        if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          return navigate("/login", { replace: true });
        }
      }
      const json = await response.json();
      const data = json.data;
      const totalPage = data.meta.total_page;
      setTotalPage(totalPage);
      if (!data.doctors) {
        setDoctorData(null);
      } else {
        setDoctorData(data);
      }
    } catch (error) {
      setDoctorData(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, searchDoctorKeyword, navigate]);

  // =====================
  // Use Effect Method
  // =====================

  useEffect(() => {
    if (searchDoctorKeyword !== "") {
      getDoctorSearchData();
    } else {
      getDoctorData();
    }
  }, [searchDoctorKeyword, getDoctorData, getDoctorSearchData]);

  // =====================
  // Render Method
  // =====================

  return isLoading ? (
    <div className="loading-container">
      <div className="loader"></div>
      <p>Sedang Memuat Mohon Menunggu</p>
    </div>
  ) : (
    <div>
      <h1>Dokter</h1>
      <DoctorListHeader
        onAddDoctorClicked={addNewDoctor}
        onSearchSpecialistEdited={searchDoctor}
      />
      <DoctorListTable
        page={page}
        doctors={doctorData.doctors}
        onEditDoctorClicked={onEditDoctor}
        onDeleteDoctorClicked={onDeleteDoctor}
      />
      <PaginationFooter
        isPreviousActive={page > 1}
        isNextActive={page < totalPage}
        onNextClick={onNextPageClicked}
        onPrevClick={onPreviousPageClicked}
      />
      <ModalAddDoctor
        isShowModal={isOpenModalAddDoctor}
        onCloseModal={onCloseModalAddDoctor}
        onSubmitData={onSubmitNewDoctor}
        selectedDoctor={selectedDoctorData}
        key={Date.now()}
      />
      <ModalDeleteConfirmation
        isShowModal={isOpenModalDeleteDoctor}
        onCloseModal={onCloseModalDeleteDoctor}
        onDeleteConfirmed={onDeleteDoctorConfirmed}
        idToDelete={idDoctorToDelete}
      />
    </div>
  );
};

export default DoctorList;
